import type { Customer } from "@agency/core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link2, Plus, Unlink, Users } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { callAction } from "../api";
import { useToast } from "../components/Toast";
import { Badge, Button, Card, CardHeader, Input, cn } from "../components/ui";
import { useT } from "../i18n";

/** Household card on the customer file: the people linked to this one, link another by search, unlink. */
type Members = { householdId: string | null; members: Array<{ id: string; name: string; policyCount: number; status: string }> };

export function HouseholdCard({ customer }: { customer: Customer }) {
  const t = useT();
  const { toast } = useToast();
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [q, setQ] = useState("");
  const household = useQuery({ queryKey: ["household", customer.id], queryFn: async () => (await callAction<"agency.household.get", Members>("agency.household.get", { customerId: customer.id })).result });
  const search = useQuery({ queryKey: ["customers", "household-search", q], enabled: adding && q.trim().length >= 2, queryFn: async () => (await callAction<"agency.customer.search", Array<{ id: string; name: string; phone?: string }>>("agency.customer.search", { q, limit: 8 })).result });
  const refresh = () => { qc.invalidateQueries({ queryKey: ["household"] }); qc.invalidateQueries({ queryKey: ["customer"] }); };
  const link = useMutation({ mutationFn: (otherId: string) => callAction("agency.household.link", { customerIds: [customer.id, otherId] }), onSuccess: () => { toast({ title: t("extras.household.linked"), tone: "success" }); setAdding(false); setQ(""); refresh(); }, onError: (e) => toast({ title: t("common.error"), body: (e as Error).message, tone: "error" }) });
  const unlink = useMutation({ mutationFn: () => callAction("agency.household.unlink", { customerId: customer.id }), onSuccess: () => { toast({ title: t("extras.household.linked"), tone: "success" }); refresh(); }, onError: (e) => toast({ title: t("common.error"), body: (e as Error).message, tone: "error" }) });
  const members = household.data?.members ?? [];
  return (
    <Card>
      <CardHeader title={<span className="inline-flex items-center gap-2"><Users className="h-4 w-4" />{t("extras.household")}</span>} subtitle={t("extras.household.hint")} action={<Button size="sm" variant="outline" onClick={() => setAdding((v) => !v)}><Plus className="h-4 w-4" />{t("extras.household.add")}</Button>} />
      <div className="px-5 pb-4">
        {members.length === 0 ? <p className="text-sm text-ink/55">{t("extras.household.none")}</p> : (
          <ul className="divide-y divide-ink/5">
            {members.map((m) => (
              <li key={m.id} className="flex items-center gap-3 py-2 text-sm">
                <Link to={`/customers/${m.id}`} className="font-semibold hover:underline">{m.name}</Link>
                <Badge>{m.status}</Badge>
                <span className="text-xs text-ink/55">{t("extras.household.policies").replace("{n}", String(m.policyCount))}</span>
              </li>
            ))}
          </ul>
        )}
        {members.length ? <Button size="sm" variant="ghost" className="mt-2" onClick={() => unlink.mutate()} loading={unlink.isPending}><Unlink className="h-4 w-4" />{t("extras.household.unlink")}</Button> : null}
        {adding ? (
          <div className="mt-3 rounded-xl bg-ink/[0.03] p-3">
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("extras.household.search")} aria-label={t("extras.household.search")} />
            {search.data?.length ? (
              <ul className="mt-2 divide-y divide-ink/5 rounded-xl border border-ink/10 bg-white text-sm">
                {search.data.filter((c) => c.id !== customer.id).map((c) => (
                  <li key={c.id} className={cn("flex items-center justify-between gap-2 px-3 py-2")}>
                    <span>{c.name}{c.phone ? ` · ${c.phone}` : ""}</span>
                    <Button size="sm" onClick={() => link.mutate(c.id)} loading={link.isPending}><Link2 className="h-3.5 w-3.5" />{t("extras.household.link")}</Button>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        ) : null}
      </div>
    </Card>
  );
}
